import { useState, useEffect } from 'react';
import { client } from '@/api/client';

// Load company settings once — shared hook
export function useCompanyInfo() {
  const [company, setCompany] = useState({});
  useEffect(() => {
    client.entities.CompanySettings.list().then(settings => {
      const get = (key) => settings.find(s => s.key === key)?.value || '';
      setCompany({
        name: get('company_name'),
        usdot: get('usdot_number'),
        mc_number: get('mc_number'),
        address: get('company_address'),
        city: get('company_city_state'),
        phone: get('company_phone'),
        email: get('company_email'),
        website: get('company_website'),
        logo_url: get('company_logo_url'),
      });
    });
  }, []);
  return company;
}

// cell can be a plain string/number OR { value, class }
function renderCell(cell) {
  if (cell === null || cell === undefined) return '<td></td>';
  if (typeof cell === 'object' && cell !== null && 'value' in cell) {
    return `<td class="${cell.class || ''}">${cell.value ?? ''}</td>`;
  }
  return `<td>${cell}</td>`;
}

// summaryCards: [{ label, value, color }] — color: 'positive' | 'negative' | 'gold'
export function generatePrintHTML(title, period, columns, rows, totalsRow, company = {}, summaryCards = null) {
  const logoHTML = company.logo_url
    ? `<img src="${company.logo_url}" style="height:48px;object-fit:contain;" alt="Logo" />`
    : `<span style="font-size:36px;line-height:1">🚛</span>`;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}${company.name ? ' - ' + company.name : ''}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; background: white; color: #1a1a2e; }

    .accent-bar { height: 6px; background: linear-gradient(90deg, #f5a623 0%, #e8931a 50%, #1a1a2e 100%); }

    .header { padding: 32px 40px 24px; display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #e5e7eb; }
    .company-block { display: flex; align-items: flex-start; gap: 16px; }
    .company-text { display: flex; flex-direction: column; gap: 4px; }
    .company-name { font-size: 24px; font-weight: 700; color: #1a1a2e; letter-spacing: -0.5px; }
    .company-meta { font-size: 11px; color: #6b7280; line-height: 1.8; }
    .company-meta span { margin-right: 16px; }

    .report-badge { text-align: right; }
    .report-type { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.12em; color: #f5a623; background: #fff8ed; border: 1px solid #f5a623; padding: 4px 12px; border-radius: 20px; display: inline-block; margin-bottom: 8px; }
    .report-title { font-size: 18px; font-weight: 700; color: #1a1a2e; }
    .report-period { font-size: 12px; color: #6b7280; margin-top: 4px; }

    .summary { display: flex; gap: 0; border-bottom: 1px solid #e5e7eb; }
    .summary-card { flex: 1; padding: 20px 24px; border-right: 1px solid #e5e7eb; }
    .summary-card:last-child { border-right: none; }
    .summary-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #9ca3af; margin-bottom: 6px; }
    .summary-value { font-size: 22px; font-weight: 700; color: #1a1a2e; }
    .summary-value.positive { color: #059669; }
    .summary-value.negative { color: #dc2626; }
    .summary-value.gold { color: #f5a623; }

    .table-section { padding: 24px 40px; }
    .table-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: #9ca3af; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    thead tr { background: #1a1a2e; }
    thead th { padding: 10px 14px; text-align: left; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #f5a623; }
    tbody tr:nth-child(even) { background: #f9fafb; }
    tbody tr:nth-child(odd) { background: white; }
    td { padding: 10px 14px; color: #374151; border-bottom: 1px solid #f3f4f6; }
    td.positive { color: #059669; font-weight: 600; }
    td.negative { color: #dc2626; font-weight: 600; }
    td.bold { font-weight: 700; color: #1a1a2e; }
    td.gold { color: #f5a623; font-weight: 700; }

    .totals-row { background: #1a1a2e !important; }
    .totals-row td { color: white !important; font-weight: 700; border: none; padding: 12px 14px; }
    .totals-row td.gold { color: #f5a623 !important; }
    .totals-row td.positive { color: #4ade80 !important; }
    .totals-row td.negative { color: #f87171 !important; }

    .footer { margin: 0 40px; padding: 16px 0; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
    .footer-left { font-size: 10px; color: #9ca3af; line-height: 1.8; }
    .footer-right { text-align: right; font-size: 10px; color: #9ca3af; }
    .footer-logo { font-size: 11px; font-weight: 700; color: #1a1a2e; letter-spacing: 0.05em; margin-bottom: 2px; }
    .footer-logo span { color: #f5a623; }
    .bottom-bar { height: 4px; background: linear-gradient(90deg, #1a1a2e 0%, #f5a623 100%); margin-top: 24px; }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @page { margin: 0; size: letter landscape; }
    }
  </style>
</head>
<body>
  <div class="accent-bar"></div>

  <div class="header">
    <div class="company-block">
      ${logoHTML}
      <div class="company-text">
        ${company.name ? `<div class="company-name">${company.name}</div>` : ''}
        <div class="company-meta">
          ${company.address ? `<span>📍 ${company.address}${company.city ? ', ' + company.city : ''}</span>` : (company.city ? `<span>📍 ${company.city}</span>` : '')}
          ${company.usdot ? `<span>USDOT: ${company.usdot}</span>` : ''}
          ${company.mc_number ? `<span>MC: ${company.mc_number}</span>` : ''}
          ${(company.address || company.city || company.usdot || company.mc_number) && (company.email || company.phone) ? '<br/>' : ''}
          ${company.email ? `<span>✉ ${company.email}</span>` : ''}
          ${company.phone ? `<span>📞 ${company.phone}</span>` : ''}
        </div>
      </div>
    </div>
    <div class="report-badge">
      <div class="report-type">Official Report</div>
      <div class="report-title">${title}</div>
      ${period ? `<div class="report-period">Period: ${period}</div>` : ''}
    </div>
  </div>

  ${summaryCards && summaryCards.length ? `
  <div class="summary">
    ${summaryCards.map(card => `
      <div class="summary-card">
        <div class="summary-label">${card.label}</div>
        <div class="summary-value ${card.color || ''}">${card.value}</div>
      </div>
    `).join('')}
  </div>` : ''}

  <div class="table-section">
    <div class="table-label">Report Details</div>
    <table>
      <thead>
        <tr>${columns.map(c => `<th>${c}</th>`).join('')}</tr>
      </thead>
      <tbody>
        ${rows.map(r => `<tr>${r.map(cell => renderCell(cell)).join('')}</tr>`).join('')}
        ${totalsRow ? `<tr class="totals-row">${totalsRow.map(cell => renderCell(cell)).join('')}</tr>` : ''}
      </tbody>
    </table>
  </div>

  <div class="footer">
    <div class="footer-left">
      <div class="footer-logo">Pro<span>Haul</span></div>
      Powered by Pro Haul · Built for modern carriers
    </div>
    <div class="footer-right">
      Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}<br/>
      ${company.website || ''}
    </div>
  </div>

  <div class="bottom-bar"></div>
</body>
</html>`;
}

export function openPrint(html) {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  document.body.appendChild(iframe);
  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();
  setTimeout(() => {
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 500);
}